'use client';

import Icon from '@/components/ui/AppIcon';
import Link from 'next/link';

interface SuccessModalProps {
  isOpen: boolean;
  orderDetails: {
    orderId: string;
    email: string;
    estimatedDelivery: string;
    paymentUrl?: string;
  };
}

export default function SuccessModal({
  isOpen,
  orderDetails,
}: SuccessModalProps) {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8 animate-in fade-in zoom-in duration-300">
        <div className="text-center">
          {/* Success Icon */}
          <div className="w-20 h-20 bg-[#42AF47]/10 rounded-full flex items-center justify-center mx-auto mb-6">
            <Icon
              name="CheckCircleIcon"
              size={48}
              className="text-[#42AF47]"
            />
          </div>

          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            Your Order Is Confirmed
          </h2>
          <p className="text-sm text-gray-600 mb-6">
            Thank you for choosing ParkPing. We’ve successfully received your
            order and it’s now being processed.
          </p>

          {/* Order Details */}
          <div className="bg-white rounded-lg p-4 mb-6 text-left space-y-3 border border-gray-200">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Order ID</span>
              <span className="text-sm font-mono font-semibold text-gray-900">
                {orderDetails.orderId}
              </span>
            </div>

            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Confirmation Email</span>
              <span className="text-sm font-medium text-gray-900">
                {orderDetails.email}
              </span>
            </div>

            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Estimated Delivery</span>
              <span className="text-sm font-medium text-gray-900">
                {orderDetails.estimatedDelivery}
              </span>
            </div>
          </div>

          {/* Next Steps */}
          <div className="space-y-3">
            <div className="bg-[#42AF47]/10 rounded-lg p-4 border border-[#42AF47]/20">
              <div className="flex items-start gap-3">
                <Icon
                  name="InformationCircleIcon"
                  size={20}
                  className="text-[#42AF47] flex-shrink-0 mt-0.5"
                />
                <div className="text-left">
                  <p className="text-sm font-medium text-[#42AF47] mb-1">
                    What happens next
                  </p>
                  <ul className="text-xs text-gray-700 space-y-1">
                    <li>• You’ll receive an order confirmation email shortly</li>
                    <li>• A tracking link will be shared once your card is shipped</li>
                    <li>• Activate your ParkPing card instantly after delivery</li>
                  </ul>
                </div>
              </div>
            </div>

          {/* Primary CTA */}
          {orderDetails.paymentUrl ? (
            <a
              href={orderDetails.paymentUrl}
              target="_blank"
              rel="noreferrer"
              className="block w-full px-6 py-3 bg-[#42AF47] text-white font-semibold rounded-lg hover:brightness-110 hover:-translate-y-0.5 transition text-center"
            >
              Proceed to Payment
            </a>
          ) : (
            <Link
              href="/homepage"
              className="block w-full px-6 py-3 bg-[#42AF47] text-white font-semibold rounded-lg hover:brightness-110 hover:-translate-y-0.5 transition text-center"
            >
              Go to Home
            </Link>
          )}

            {/* Secondary CTA */}
            <Link
              href="/support-hub"
              className="block w-full px-6 py-3 border border-gray-200 text-gray-900 font-semibold rounded-lg hover:bg-gray-50 transition text-center"
            >
              Need Help? Contact Support
            </Link>

            <p className="text-xs text-gray-500 mt-6 text-center">
              Secure payment • Privacy protected • Dedicated support
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
